import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import "./LoginCaptcha.css";

const CAPTCHA_LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const CAPTCHA_DIGITS = "0123456789";
const CAPTCHA_CHARS = CAPTCHA_LETTERS + CAPTCHA_DIGITS;
const CAPTCHA_LENGTH = 5;

const pick = (chars) =>
  chars.charAt(Math.floor(Math.random() * chars.length));

/** Always mix letters + numbers so numeric digits are included. */
const randomCode = () => {
  const chars = [];
  // Guarantee at least 2 digits and 2 letters
  chars.push(pick(CAPTCHA_DIGITS));
  chars.push(pick(CAPTCHA_DIGITS));
  chars.push(pick(CAPTCHA_LETTERS));
  chars.push(pick(CAPTCHA_LETTERS));
  while (chars.length < CAPTCHA_LENGTH) {
    chars.push(pick(CAPTCHA_CHARS));
  }
  // Shuffle
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
};

/**
 * Frontend-only canvas CAPTCHA (CWE-307 mitigation for login).
 * Text is drawn on canvas so it cannot be selected/copied as HTML text.
 */
const LoginCaptcha = forwardRef(function LoginCaptcha({ onChange }, ref) {
  const canvasRef = useRef(null);
  const codeRef = useRef("");
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState("");

  const drawCaptcha = useCallback((code) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = "#eef2f7";
    ctx.fillRect(0, 0, width, height);

    // Noise lines
    for (let i = 0; i < 6; i += 1) {
      ctx.strokeStyle = `rgba(${80 + Math.random() * 100},${
        80 + Math.random() * 100
      },${80 + Math.random() * 100},0.45)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Noise dots
    for (let i = 0; i < 40; i += 1) {
      ctx.fillStyle = `rgba(0,0,0,${0.08 + Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * width,
        Math.random() * height,
        1 + Math.random() * 1.5,
        0,
        Math.PI * 2
      );
      ctx.fill();
    }

    // Characters (canvas pixels — not copyable DOM text)
    const charWidth = width / (code.length + 1);
    for (let i = 0; i < code.length; i += 1) {
      const char = code[i];
      const fontSize = 22 + Math.floor(Math.random() * 6);
      ctx.save();
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.fillStyle = `rgb(${20 + Math.random() * 60},${
        20 + Math.random() * 60
      },${80 + Math.random() * 80})`;
      ctx.translate(charWidth * (i + 0.7), height / 2 + 6);
      ctx.rotate((Math.random() - 0.5) * 0.5);
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }
  }, []);

  const refresh = useCallback(() => {
    const next = randomCode();
    codeRef.current = next;
    setInputValue("");
    setError("");
    drawCaptcha(next);
    if (onChange) onChange("");
  }, [drawCaptcha, onChange]);

  useEffect(() => {
    const next = randomCode();
    codeRef.current = next;
    drawCaptcha(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => {
        const entered = inputValue.trim().toUpperCase();
        if (!entered) {
          setError("Captcha is required");
          return false;
        }
        if (entered !== codeRef.current) {
          setError("Captcha does not match");
          refresh();
          return false;
        }
        setError("");
        return true;
      },
      refresh,
    }),
    [inputValue, refresh]
  );

  const blockCopy = (e) => {
    e.preventDefault();
    return false;
  };

  return (
    <div className="login-captcha" onCopy={blockCopy} onCut={blockCopy}>
      <div className="login-captcha-row">
        <canvas
          ref={canvasRef}
          className="login-captcha-canvas"
          width={140}
          height={44}
          aria-label="Captcha image"
          onContextMenu={blockCopy}
          onDragStart={blockCopy}
          onCopy={blockCopy}
        />
        <button
          type="button"
          className="login-captcha-refresh"
          onClick={refresh}
          aria-label="Refresh captcha"
          title="Refresh captcha"
        >
          ↻
        </button>
      </div>
      <div className={`input-field ${error ? "login-captcha-error-field" : ""}`}>
        <input
          id="loginCaptcha"
          name="loginCaptcha"
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="Enter captcha"
          value={inputValue}
          maxLength={CAPTCHA_LENGTH}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            setInputValue(value);
            setError("");
            if (onChange) onChange(value);
          }}
          onPaste={blockCopy}
        />
      </div>
      {error && (
        <div className="error-message">
          <span>{error}</span>
        </div>
      )}
    </div>
  );
});

export default LoginCaptcha;
